// Define the InventoryResponse interface
export interface InventoryResponse {
  success: boolean;
  items?: any[];
  continuationToken?: string | null;
}

// Keep this interface since it's used by updatePlayerTitleData
export interface UpdateTitleDataResponse {
  success: boolean;
  data?: any;
}

// Add this interface at the top
interface CloudScriptRequest {
  FunctionName: string;
  FunctionParameter: {
    [key: string]: any;
  };
}

// Define the structure of the data returned by GetUserData
interface UserDataRecord {
  Value: string;
  LastUpdated: string;
  Permission: string; // Usually 'Private' or 'Public'
}

// Add export to make it a module
export class PlayFab {
  // Add this new property near the top of the class
  private static isInitialized: boolean = false;

  // Add this new method
  private static initialize() {
    if (!this.isInitialized) {
      this.loadSession();
      this.isInitialized = true;
    }
  }

  protected static titleId = '191E8E';
  protected static playerId = '';
  protected static sessionTicket = '';
  protected static entityToken = '';
  protected static entity: any = null;

  public static post(method: string, body: any): Promise<{ status: number, body?: any }> {
    this.initialize();
    return new Promise((resolve, reject) => {
      var request = new XMLHttpRequest();
      request.open('POST', `https://${PlayFab.titleId}.playfabapi.com/${method}`);
      request.setRequestHeader('Content-Type', 'application/json');

      if (PlayFab.sessionTicket != '')
        request.setRequestHeader('X-Authorization', PlayFab.sessionTicket);

      if (PlayFab.entityToken != '')
        request.setRequestHeader('X-EntityToken', PlayFab.entityToken);
      
      console.log(`> PLAYFAB /${method}`, body);
      
      let payload = JSON.stringify(body);
      request.send(payload);
  
      request.onload = () => {
        let responseBody = null;
        if (request.responseText != '')
          responseBody = JSON.parse(request.responseText);
  
        console.log('< PLAYFAB', responseBody);
  
        resolve({
          status: request.status,
          body: responseBody
        });
      };
  
      request.onerror = (e) => {
        console.log('< ERROR');
        resolve({
          status: 500,
          body: {message: 'Internal service error!'}
        });
      };
    });
  }

  public static async loginWithCustomID(customId: string): Promise<{ success: boolean, newAccount?: boolean }> {
    let response = await PlayFab.post('Client/LoginWithCustomID', {
      TitleId: PlayFab.titleId,
      CustomId: customId,
      CreateAccount: true
    });

    if (response.status == 200) {
      PlayFab.playerId = response.body.data.PlayFabId;
      PlayFab.sessionTicket = response.body.data.SessionTicket;
      PlayFab.entity = response.body.data.EntityToken.Entity;
      PlayFab.entityToken = response.body.data.EntityToken.EntityToken;
      PlayFab.storeSession();
      return { success: true, newAccount: response.body.data.NewlyCreated };
    }

    return { success: false };
  }

  public static async updateUserData(data: { [key: string]: string }, permission: "Public" | "Private" = "Private"): Promise<boolean> {
    const response = await this.post('Client/UpdateUserData', {
      Data: data,
      Permission: permission
    });

    return response.status === 200;
  }

  public static async loginWithTwitch(accessToken: string): Promise<{ success: boolean }> {
    let response = await PlayFab.post('Client/LoginWithTwitch', {
      TitleId: PlayFab.titleId, 
      AccessToken: accessToken,
      CreateAccount: true
    });

    if (response.status == 200) {
      PlayFab.playerId = response.body.data.PlayFabId;
      PlayFab.sessionTicket = response.body.data.SessionTicket;
      PlayFab.entity = response.body.data.EntityToken.Entity;
      PlayFab.entityToken = response.body.data.EntityToken.EntityToken;
    }

    return { success: response.status == 200 };
  }

  public static async loginWithOpenIdConnect(idToken: string, provider: string): Promise<{ success: boolean }> {
    if (this.loadSession()) {
      return { success: true };
    }

    let response = await this.post('Client/LoginWithOpenIdConnect', {
      TitleId: this.titleId,
      ConnectionId: provider,
      IdToken: idToken,
      CreateAccount: true
    });

    if (response.status == 200) {
      this.playerId = response.body.data.PlayFabId;
      this.sessionTicket = response.body.data.SessionTicket;
      this.entity = response.body.data.EntityToken.Entity;
      this.entityToken = response.body.data.EntityToken.EntityToken;
      this.storeSession();
    }

    return { success: response.status == 200 };
  }

  public static async getInventory(continuationToken: string | null = null): Promise<InventoryResponse> {
    let body: any = {};
    if (continuationToken) {
      body.ContinuationToken = continuationToken;
    }

    let response = await PlayFab.post('Inventory/GetInventoryItems', body);

    if (response.status != 200) {
      return { success: false };
    }

    return {
      success: true,
      items: response.body.data.Items,
      continuationToken: response.body.data.ContinuationToken
    };
  }

  public static async getCurrencyItems(): Promise<InventoryResponse> {
    const response = await PlayFab.post('Inventory/GetInventoryItems', {
      Filter: "type eq 'currency'"
    });

    if (response.status === 200 && response.body?.data) {
      return {
        success: true,
        items: response.body.data.Items,
        continuationToken: response.body.data.ContinuationToken
      };
    }

    return { success: false, items: [] };
  }

  public static async getUserReadOnlyData(keys: string[]): Promise<{ success: boolean, data?: any }> {
    let response = await PlayFab.post('Client/GetUserReadOnlyData', { Keys: keys });

    if (response.status != 200)
      return { success: false };

    return { success: true, data: response.body.data.Data };
  }

  public static async executeCloudScript(
    funcOrRequest: string | CloudScriptRequest,
    params?: any
  ): Promise<{ success: boolean, data?: any }> {
    let functionName: string;
    let functionParameter: any;

    // Handle both calling styles
    if (typeof funcOrRequest === 'string') {
      // Old style: executeCloudScript(functionName, params)
      functionName = funcOrRequest;
      functionParameter = params || {};
    } else {
      // New style: executeCloudScript({ FunctionName, FunctionParameter })
      functionName = funcOrRequest.FunctionName;
      functionParameter = funcOrRequest.FunctionParameter;
    }

    let response = await PlayFab.post('Client/ExecuteCloudScript', {
      FunctionName: functionName,
      FunctionParameter: functionParameter,
      GeneratePlayStreamEvent: true
    });

    if (response.status != 200)
      return { success: false };

    return { success: true, data: response.body.data };
  }

  public static getPlayerId(): string {
    this.initialize();
    return PlayFab.playerId;
  }

  public static getEntity(): any {
    this.initialize();
    return this.entity;
  }

  public static getPlayFabId(): string {
    this.initialize();
    return this.playerId;
  }

  public static async getAccountInfo(): Promise<{ status: number, body?: any }> {
    return PlayFab.post('Client/GetAccountInfo', {});
  }

  private static getSessionTicket(): string {
    // Implement this method to return the current PlayFab session ticket
    // This might be stored in localStorage or in memory after login
    return ''; // Placeholder return, replace with actual implementation
  }

  public static async getUserAccountInfoByDisplayName(displayName: string) {
    try {
      const response = await this.post('Client/GetAccountInfo', {
        TitleDisplayName: displayName
      });

      if (response.status === 200 && response.body?.data?.AccountInfo) {
        return {
          success: true,
          accountInfo: response.body.data.AccountInfo
        };
      } else {
        return {
          success: false,
          error: 'Failed to retrieve user account info'
        };
      }
    } catch (error) {
      console.error('Error in getUserAccountInfoByDisplayName:', error);
      return {
        success: false,
        error: 'An error occurred while fetching user account info'
      };
    }
  }

  private static storeSession() {
    const session = {
      playerId: this.playerId,
      sessionTicket: this.sessionTicket,
      entity: this.entity,
      entityToken: this.entityToken,
      timestamp: Date.now()
    };
    localStorage.setItem('PlayFabSession', JSON.stringify(session));
  }

  private static loadSession(): boolean {
    const sessionData = localStorage.getItem('PlayFabSession');
    if (sessionData) {
      const session = JSON.parse(sessionData);
      const currentTime = Date.now();
      // Check if the session is less than 23 hours old (giving 1 hour buffer)
      if (currentTime - session.timestamp < 23 * 60 * 60 * 1000) {
        this.playerId = session.playerId;
        this.sessionTicket = session.sessionTicket;
        this.entity = session.entity;
        this.entityToken = session.entityToken;
        return true;
      }
    }
    return false;
  }

  public static clearSession() {
    localStorage.removeItem('PlayFabSession');
    this.playerId = '';
    this.sessionTicket = '';
    this.entity = null;
    this.entityToken = '';
  }

  public static hasValidSession(): boolean {
    this.initialize();
    return !!this.entityToken;
  }

  public static async updateAvatarUrl(avatarUrl: string): Promise<boolean> {
    try {
      console.log("Updating avatar URL in PlayFab:", avatarUrl);
      const response = await this.post('Client/UpdateAvatarUrl', {
        ImageUrl: avatarUrl
      });

      if (response.status === 200) {
        console.log('Avatar URL updated successfully in PlayFab');
        return true;
      } else {
        console.error('Failed to update avatar URL in PlayFab:', response);
        return false;
      }
    } catch (error) {
      console.error('Error updating avatar URL in PlayFab:', error);
      return false;
    }
  }

  public static async getPlayerProfile(): Promise<any> {
    if (!this.hasValidSession()) {
      throw new Error('No valid PlayFab session');
    }

    try {
      console.log("Fetching player profile from PlayFab");
      const response = await this.post('Client/GetPlayerProfile', {
        PlayFabId: this.getPlayerId(),
        ProfileConstraints: {
          ShowDisplayName: true  // Only include ShowDisplayName as it's the only allowed constraint
        }
      });

      if (response.status === 200 && response.body.data && response.body.data.PlayerProfile) {
        console.log("Player profile fetched successfully:", response.body.data.PlayerProfile);
        return response.body.data.PlayerProfile;
      } else {
        console.error('Failed to fetch player profile:', response);
        throw new Error('Failed to fetch player profile');
      }
    } catch (error) {
      console.error('Error fetching player profile:', error);
      throw error;
    }
  }

  public static async updatePlayerTitleData(request: { Data: { [key: string]: string } }): Promise<UpdateTitleDataResponse> {
    try {
      console.log('Raw update request:', request);
      const response = await this.post('Client/UpdateUserData', {
        Data: request.Data,
        Permission: "Public"
      });

      console.log('Update response:', response);

      if (response.status === 200 && response.body?.data) {
        return {
          success: true,
          data: response.body.data
        };
      } else {
        return {
          success: false
        };
      }
    } catch (error) {
      console.error('Error updating player title data:', error);
      return {
        success: false
      };
    }
  }

  public static async getUserTitleData(keys: string[]): Promise<{ success: boolean, data?: { [key: string]: UserDataRecord } }> {
    try {
      console.log('Getting user title data for keys:', keys);
      const response = await this.post('Client/GetUserData', {
        Keys: keys
      });

      console.log('PlayFab get response:', response);

      if (response.status === 200 && response.body?.data?.Data) {
        return {
          success: true,
          data: response.body.data.Data as { [key: string]: UserDataRecord }
        };
      } else {
        return {
          success: false
        };
      }
    } catch (error) {
      console.error('Error getting user title data:', error);
      return {
        success: false
      };
    }
  }

  public static async getCatalogItem(itemId: string): Promise<any> {
    const response = await PlayFab.post('Client/GetItem', {
      ItemId: itemId
    });

    console.log('Item Response:', response);
    
    if (response.status === 200 && response.body?.data?.Item) {
      return response.body.data.Item;
    }
    
    return null;
  }

  public static async getCatalogItems(itemIds: string[]): Promise<any[]> {
    const response = await PlayFab.post('Catalog/GetItems', {
      Ids: itemIds
    });

    console.log('Items Response:', response);
    
    if (response.status === 200 && response.body?.data?.Items) {
      return response.body.data.Items;
    }
    
    return [];
  }

  // Change this method to use getUserReadOnlyData
  public static async getOtherUserReadOnlyData(playerId: string, keys: string[]): Promise<{ success: boolean, data?: any }> {
    try {
        console.log('Fetching data for player:', playerId, 'keys:', keys);
        
        const response = await PlayFab.post('Client/GetUserReadOnlyData', {
            PlayFabId: playerId,
            Keys: keys
        });

        console.log('Raw PlayFab response:', response);

        if (response.status !== 200) {
            console.error('Invalid response status:', response.status);
            return { success: false };
        }

        // Change how we access the data based on the actual response structure
        const data = response.body?.data?.Data;
        if (!data) {
            console.error('No data in response');
            return { success: false };
        }

        return { 
            success: true, 
            data: data
        };
    } catch (error) {
        console.error('Error in getOtherUserReadOnlyData:', error);
        return { success: false };
    }
  }
}

